import type { GeometryStatus } from "@/api/types";

export interface GeoStyle {
  label: string;
  stroke: string;
  dash: string;
  fill: string;
  vertex: string;
  badge: string;      // tailwind classes
  text: string;       // text color class
  dot: string;        // bg color class for dot
}

export const GEO_STYLES: Record<GeometryStatus, GeoStyle> = {
  estimated: {
    label: "Estimée",
    stroke: "#F59E0B",
    dash: "6,4",
    fill: "rgba(245, 158, 11, 0.08)",
    vertex: "#FBBF24",
    badge: "bg-amber-500/15 text-amber-400 border border-amber-500/30",
    text: "text-amber-400",
    dot: "bg-amber-400",
  },
  reconstructed: {
    label: "Reconstruite",
    stroke: "#06B6D4",
    dash: "none",
    fill: "rgba(6, 182, 212, 0.12)",
    vertex: "#22D3EE",
    badge: "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30",
    text: "text-cyan-400",
    dot: "bg-cyan-400",
  },
  validated: {
    label: "Validée",
    stroke: "#10B981",
    dash: "none",
    fill: "rgba(16, 185, 129, 0.15)",
    vertex: "#34D399",
    badge: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
    text: "text-emerald-400",
    dot: "bg-emerald-400",
  },
  conflict: {
    label: "Conflit",
    stroke: "#EF4444",
    dash: "2,2",
    fill: "rgba(239, 68, 68, 0.2)",
    vertex: "#F87171",
    badge: "bg-rose-500/15 text-rose-400 border border-rose-500/30 animate-pulse",
    text: "text-rose-400",
    dot: "bg-rose-400",
  },
};

export type WallKind = "exterior" | "bearing" | "partition";

export interface WallKindStyle {
  label: string;      // French label
  short: string;      // short label for canvas
  def: number;        // default thickness in meters
  fill: string;       // band fill color
  opacity: number;    // band fill opacity
}

export const WALL_KINDS: Record<WallKind, WallKindStyle> = {
  exterior:  { label: "Mur extérieur", short: "Ext.", def: 0.30, fill: "#94A3B8", opacity: 0.6 },
  bearing:   { label: "Mur porteur",   short: "Port.", def: 0.20, fill: "#64748B", opacity: 0.5 },
  partition: { label: "Cloison",       short: "Clois.", def: 0.07, fill: "#CBD5E1", opacity: 0.32 },
};

export const DEFAULT_WALL_KIND: WallKind = "bearing";
export const WALL_THICKNESS_MIN = 0.05;
export const WALL_THICKNESS_MAX = 0.50;

export const wallKindOf = (k?: string): WallKind =>
  (k === "exterior" || k === "bearing" || k === "partition" ? k : DEFAULT_WALL_KIND);
export const wallThicknessOf = (w: { kind?: string; thickness?: number }): number =>
  typeof w.thickness === "number" && w.thickness > 0 ? w.thickness : WALL_KINDS[wallKindOf(w.kind)].def;

// ---- Quantity take-off (zones, perimeter, wall linear metres) ----
type Pt = { x: number; y: number };
type GVertex = { id: string; x: number; y: number };
type GWall = { id: string; from: string; to: string; kind?: string; thickness?: number };
export interface ZoneDef { name: string; x: number; y: number }
export interface FaceRegion { area: number; cx: number; cy: number; poly: Pt[] }

export function floorRegions(vertices: GVertex[], walls: GWall[]): { outer: FaceRegion | null; regions: FaceRegion[] } {
  const pos = new Map<string, Pt>();
  for (const v of vertices) pos.set(v.id, { x: v.x, y: v.y });
  const nbr = new Map<string, string[]>();
  const addEdge = (a: string, b: string) => {
    if (a === b || !pos.has(a) || !pos.has(b)) return;
    if (!nbr.has(a)) nbr.set(a, []);
    if (!nbr.get(a)!.includes(b)) nbr.get(a)!.push(b);
  };
  for (const w of walls) { addEdge(w.from, w.to); addEdge(w.to, w.from); }
  if (nbr.size < 3) return { outer: null, regions: [] };
  for (const [v, list] of Array.from(nbr.entries())) {
    const p = pos.get(v)!;
    list.sort((m, n) => Math.atan2(pos.get(m)!.y - p.y, pos.get(m)!.x - p.x) - Math.atan2(pos.get(n)!.y - p.y, pos.get(n)!.x - p.x));
  }
  const visited = new Set<string>();
  const key = (a: string, b: string) => a + ">" + b;
  const faces: FaceRegion[] = [];
  const maxSteps = walls.length * 2 + 5;
  for (const w of walls) {
    for (const [sa, sb] of [[w.from, w.to], [w.to, w.from]] as const) {
      if (!pos.has(sa) || !pos.has(sb) || sa === sb || visited.has(key(sa, sb))) continue;
      const poly: Pt[] = [];
      let u = sa, v = sb, guard = 0, ok = true;
      while (guard++ <= maxSteps) {
        visited.add(key(u, v));
        poly.push(pos.get(u)!);
        const list = nbr.get(v)!;
        const idx = list.indexOf(u);
        if (idx < 0) { ok = false; break; }
        const next = list[(idx - 1 + list.length) % list.length];
        u = v; v = next;
        if (u === sa && v === sb) break;
      }
      if (!ok || poly.length < 3) continue;
      let A2 = 0, cx = 0, cy = 0;
      for (let i = 0; i < poly.length; i++) {
        const p = poly[i], q = poly[(i + 1) % poly.length];
        const cr = p.x * q.y - q.x * p.y;
        A2 += cr; cx += (p.x + q.x) * cr; cy += (p.y + q.y) * cr;
      }
      const area = Math.abs(A2 / 2);
      if (area > 1e-3) faces.push({ area, cx: cx / (3 * A2), cy: cy / (3 * A2), poly });
    }
  }
  if (!faces.length) return { outer: null, regions: [] };
  let outerIdx = 0;
  for (let i = 1; i < faces.length; i++) if (faces[i].area > faces[outerIdx].area) outerIdx = i;
  const regions = faces.filter((_, i) => i !== outerIdx);
  return { outer: faces[outerIdx], regions: regions.length ? regions : [faces[outerIdx]] };
}

export function pointInPoly(pt: Pt, poly: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if ((yi > pt.y) !== (yj > pt.y) && pt.x < ((xj - xi) * (pt.y - yi)) / ((yj - yi) || 1e-12) + xi) inside = !inside;
  }
  return inside;
}

export function resolveZoneName(region: FaceRegion, zones: ZoneDef[] | undefined, index: number): string {
  for (const z of zones || []) if (pointInPoly({ x: z.x, y: z.y }, region.poly)) return z.name || `Zone ${index + 1}`;
  return `Zone ${index + 1}`;
}

export function polyPerimeter(poly: Pt[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    s += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return s;
}

export function polyArea(poly: Pt[]): number {
  let A = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    A += a.x * b.y - b.x * a.y;
  }
  return Math.abs(A) / 2;
}

export function wallLengthByKind(vertices: GVertex[], walls: GWall[]): Record<WallKind, number> {
  const pos = new Map<string, Pt>();
  for (const v of vertices) pos.set(v.id, { x: v.x, y: v.y });
  const out: Record<WallKind, number> = { exterior: 0, bearing: 0, partition: 0 };
  for (const w of walls) {
    const a = pos.get(w.from), b = pos.get(w.to);
    if (!a || !b) continue;
    out[wallKindOf(w.kind)] += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return out;
}

export interface MetreRow { name: string; area: number; habitable: number; cx: number; cy: number }

function regionHabitable(region: FaceRegion, vertices: GVertex[], walls: GWall[]): number {
  const idAt = (p: Pt) => vertices.find((v) => Math.hypot(v.x - p.x, v.y - p.y) < 1e-6)?.id;
  const thOf = (ia: string, ib: string) => {
    const w = walls.find((w) => (w.from === ia && w.to === ib) || (w.from === ib && w.to === ia));
    return w ? wallThicknessOf(w) : 0;
  };
  let deduct = 0;
  const poly = region.poly;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const ia = idAt(a), ib = idAt(b);
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (ia && ib) deduct += (len * thOf(ia, ib)) / 2;
  }
  return Math.max(0, region.area - deduct);
}

export function metreForGeom(geom: { vertices: GVertex[]; walls: GWall[]; zones?: ZoneDef[]; lowCeilingArea?: number; lowCeilingPolys?: Pt[][] }): {
  zones: MetreRow[]; total: number; totalHabitable: number; lowCeiling: number; carrez: number; perimeter: number; byKind: Record<WallKind, number>;
} {
  const { outer, regions } = floorRegions(geom.vertices || [], geom.walls || []);
  const zones: MetreRow[] = regions.map((r, i) => ({
    name: resolveZoneName(r, geom.zones, i), area: r.area, habitable: regionHabitable(r, geom.vertices || [], geom.walls || []), cx: r.cx, cy: r.cy,
  }));
  const total = regions.reduce((s, r) => s + r.area, 0);
  const totalHabitable = zones.reduce((s, z) => s + z.habitable, 0);
  const lcPolys = geom.lowCeilingPolys || [];
  const lowCeiling = lcPolys.length ? lcPolys.reduce((s, p) => s + polyArea(p), 0) : Math.max(0, geom.lowCeilingArea || 0);
  const carrez = Math.max(0, totalHabitable - lowCeiling);
  return { zones, total, totalHabitable, lowCeiling, carrez, perimeter: outer ? polyPerimeter(outer.poly) : 0, byKind: wallLengthByKind(geom.vertices || [], geom.walls || []) };
}

export interface Bounds { minX: number; minY: number; maxX: number; maxY: number; w: number; h: number; }

export function bounds(vertices: { x: number; y: number }[]): Bounds {
  if (!vertices.length) return { minX: 0, minY: 0, maxX: 1, maxY: 1, w: 1, h: 1 };
  const xs = vertices.map((v) => v.x);
  const ys = vertices.map((v) => v.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  return { minX, minY, maxX, maxY, w: maxX - minX || 1, h: maxY - minY || 1 };
}
